import { useEffect, useRef, useState } from 'react'
import { giveClient, readClients } from '@shared/services'

/**
 * Client, for pictures in the Egnyte Inbox (Huanrui, 2026-10-08)
 * -------------------------------------------------------------
 * Pick a client used before or type a new one; every selected picture gets
 * it, replacing any client it had. Their project letters are remembered for
 * it (MRS means Mars), so pictures with those letters that come in later get
 * it by themselves (docs/clients.sql). Laid out like BulkTagBar, in its place
 * above the tray.
 */
export function ClientBar({
  assetIds,
  onDone,
  onClose,
}: {
  assetIds: string[]
  onDone: (message: string, failed?: boolean) => void
  onClose: () => void
}) {
  const [clients, setClients] = useState<string[]>([])
  const [codes, setCodes] = useState<{ code: string; client: string }[]>([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let live = true
    readClients()
      .then((found) => {
        if (!live) return
        setClients(found.clients)
        setCodes(found.codes)
      })
      .catch(() => undefined)
    input.current?.focus()
    return () => {
      live = false
    }
  }, [])

  const name = text.trim()
  const known = clients.find((one) => one.toLowerCase() === name.toLowerCase())
  const count = assetIds.length
  const lettersOf = (client: string) =>
    codes
      .filter((row) => row.client.toLowerCase() === client.toLowerCase())
      .map((row) => row.code)
      .join(', ')

  const apply = async () => {
    if (!name || busy) return
    setBusy(true)
    try {
      const done = await giveClient(assetIds, known ?? name)
      const parts = [`${done.tagged} given ${known ?? name}.`]
      if (done.remembered.length > 0) parts.push(`${done.remembered.join(', ')} will mean ${known ?? name} from now on.`)
      if (done.already.length > 0) {
        parts.push(`${done.already.map((row) => `${row.code} already means ${row.client}`).join('; ')}: left as it was.`)
      }
      window.dispatchEvent(new Event('ww:library-changed'))
      onDone(parts.join(' '), done.already.length > 0)
      onClose()
    } catch (error) {
      onDone(error instanceof Error ? error.message : 'The client could not be given', true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="web-tray__tagbar">
      <input
        ref={input}
        className="web-tray__taginput"
        list="ww-clients"
        value={text}
        placeholder="Client, e.g. Mars"
        spellCheck={false}
        disabled={busy}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') void apply()
          if (event.key === 'Escape') onClose()
        }}
      />
      <datalist id="ww-clients">
        {clients.map((one) => (
          <option key={one} value={one} label={lettersOf(one) || undefined} />
        ))}
      </datalist>
      <button type="button" className="ww-btn ww-btn--primary ww-btn--sm" disabled={busy || !name} onClick={() => void apply()}>
        {busy ? 'Giving…' : known || !name ? `Give ${count} this client` : `New client · give ${count}`}
      </button>
      <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={onClose}>
        Done
      </button>
    </div>
  )
}
